import './styles.css';

const MAX_FILES = 8;
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_TOTAL_BYTES = 64 * 1024 * 1024;
const MAX_PIXELS = 40 * 1000 * 1000;
const MAX_DIMENSION = 16384;
const MAX_PREVIEW_EDGE = 1024;
const MAX_CONCURRENCY = 2;
const DECODE_TIMEOUT_MS = 15000;

const elements = {
  fileInput: document.querySelector('#file-input'),
  dropZone: document.querySelector('#drop-zone'),
  queueList: document.querySelector('#queue-list'),
  queueCount: document.querySelector('#queue-count'),
  queueSummary: document.querySelector('#queue-summary'),
  convertButton: document.querySelector('#convert-button'),
  clearButton: document.querySelector('#clear-button'),
  resultPanel: document.querySelector('#result-panel'),
  resultSummary: document.querySelector('#result-summary'),
  zipButton: document.querySelector('#zip-button'),
  status: document.querySelector('#status-message'),
  qualityInput: document.querySelector('#quality-input'),
  qualityValue: document.querySelector('#quality-value'),
  qualityControl: document.querySelector('#quality-control'),
};

const state = {
  queue: [],
  format: 'jpg',
  quality: 0.88,
  activeRun: null,
  revision: 0,
  inputTicket: 0,
  nextRunId: 0,
};

document.querySelectorAll('[data-year]').forEach((node) => {
  node.textContent = String(new Date().getFullYear());
});

function ascii(bytes, start, length) {
  if (start < 0 || start + length > bytes.length) return '';
  return String.fromCharCode(...bytes.subarray(start, start + length));
}

function readU32(bytes, offset) {
  return ((bytes[offset] * 0x1000000) + (bytes[offset + 1] * 0x10000) + (bytes[offset + 2] * 0x100) + bytes[offset + 3]) >>> 0;
}

function readU64(bytes, offset) {
  return (BigInt(readU32(bytes, offset)) << 32n) | BigInt(readU32(bytes, offset + 4));
}

function parseBox(bytes, offset, limit) {
  if (offset + 8 > limit) throw new Error('The AVIF container has an incomplete box header.');
  const declaredSize = readU32(bytes, offset);
  const type = ascii(bytes, offset + 4, 4);
  let headerSize = 8;
  let size;
  if (declaredSize === 1) {
    if (offset + 16 > limit) throw new Error('The AVIF container has an incomplete large box size.');
    const largeSize = readU64(bytes, offset + 8);
    if (largeSize > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error('The AVIF box is too large to inspect safely.');
    size = Number(largeSize);
    headerSize = 16;
  } else if (declaredSize === 0) {
    size = limit - offset;
  } else {
    size = declaredSize;
  }
  if (size < headerSize || size > limit - offset) throw new Error('The AVIF container has a box outside its declared bounds.');
  return { start: offset, end: offset + size, type, payloadStart: offset + headerSize, payloadEnd: offset + size };
}

function getBoxes(bytes, start, end) {
  const boxes = [];
  let offset = start;
  while (offset < end) {
    const box = parseBox(bytes, offset, end);
    boxes.push(box);
    if (box.end <= offset) throw new Error('The AVIF container has a zero-length box.');
    offset = box.end;
  }
  if (offset !== end) throw new Error('The AVIF container has invalid box padding.');
  return boxes;
}

const nestedBoxTypes = new Set([
  'meta', 'iprp', 'ipco', 'moov', 'trak', 'mdia', 'minf', 'stbl', 'edts', 'dinf',
  'mvex', 'moof', 'traf', 'mfra', 'udta', 'tref', 'meco', 'ipro', 'sinf', 'schi',
]);

function readIspeDimensions(bytes, box) {
  if (box.payloadEnd - box.payloadStart < 12) throw new Error('The AVIF image dimensions box is incomplete.');
  const width = readU32(bytes, box.payloadStart + 4);
  const height = readU32(bytes, box.payloadStart + 8);
  if (!width || !height) throw new Error('The AVIF image dimensions are invalid.');
  return { width, height };
}

function inspectBoxTree(bytes, boxes, info, depth = 0) {
  if (depth > 12) throw new Error('The AVIF container nesting is too deep.');
  for (const box of boxes) {
    info.types.add(box.type);
    if (box.type === 'meta') info.hasMeta = true;
    if (box.type === 'mdat' || box.type === 'idat') info.hasPayload = true;
    if (box.type === 'av1C') info.hasAv1C = true;
    if (box.type === 'moov' || box.type === 'trak') info.hasSequenceTrack = true;
    if (box.type === 'ispe') {
      const dimensions = readIspeDimensions(bytes, box);
      if (!info.dimensions || dimensions.width * dimensions.height > info.dimensions.width * info.dimensions.height) {
        info.dimensions = dimensions;
      }
    }
    if (box.type === 'mdcv' || box.type === 'clli') info.hasHdrMetadata = true;
    if (box.type === 'colr' && box.payloadEnd - box.payloadStart >= 12 && ascii(bytes, box.payloadStart, 4) === 'nclx') {
      const primaries = (bytes[box.payloadStart + 4] * 0x100) + bytes[box.payloadStart + 5];
      const transfer = (bytes[box.payloadStart + 6] * 0x100) + bytes[box.payloadStart + 7];
      info.color = { primaries, transfer };
      if ([16, 17, 18].includes(transfer) || [9, 10, 11, 12].includes(primaries)) info.hasHdrMetadata = true;
    }
    if (!nestedBoxTypes.has(box.type)) continue;
    let childStart = box.payloadStart;
    if (box.type === 'meta') {
      if (box.payloadEnd - childStart < 4) throw new Error('The AVIF metadata box is incomplete.');
      childStart += 4;
    }
    if (childStart >= box.payloadEnd) continue;
    const children = getBoxes(bytes, childStart, box.payloadEnd);
    inspectBoxTree(bytes, children, info, depth + 1);
  }
}

function parseAvif(bytes) {
  if (bytes.length < 16) throw new Error('This file is too small to be an AVIF image.');
  const boxes = getBoxes(bytes, 0, bytes.length);
  if (!boxes.length || boxes[0].type !== 'ftyp') throw new Error('This does not look like an AVIF file. The file contents were checked.');
  const ftyp = boxes[0];
  if (ftyp.payloadEnd - ftyp.payloadStart < 8) throw new Error('The AVIF file type box is incomplete.');
  const brands = [ascii(bytes, ftyp.payloadStart, 4)];
  for (let offset = ftyp.payloadStart + 8; offset + 4 <= ftyp.payloadEnd; offset += 4) brands.push(ascii(bytes, offset, 4));
  const hasAvifBrand = brands.includes('avif') || brands.includes('avis');
  if (!hasAvifBrand) throw new Error('The file is an ISO-BMFF container, but it is not an AVIF image.');
  if (brands.includes('avis')) throw new Error('Animated AVIF sequences are not supported. Choose a static AVIF image.');

  const info = {
    brands,
    types: new Set(),
    dimensions: null,
    hasMeta: false,
    hasAv1C: false,
    hasPayload: false,
    hasSequenceTrack: false,
    hasHdrMetadata: false,
    color: null,
    animated: false,
    hdr: false,
  };
  inspectBoxTree(bytes, boxes, info);
  if (info.hasSequenceTrack) throw new Error('Animated AVIF sequences are not supported. Choose a static AVIF image.');
  if (!info.hasMeta || !info.hasAv1C || !info.hasPayload) throw new Error('The AVIF image is incomplete or does not contain a supported AV1 image item.');
  if (!info.dimensions) throw new Error('The AVIF image does not declare usable dimensions.');
  const { width, height } = info.dimensions;
  if (width > MAX_DIMENSION || height > MAX_DIMENSION || width * height > MAX_PIXELS) {
    throw new Error(`The decoded image must be at most ${MAX_PIXELS / 1000000} megapixels and ${MAX_DIMENSION} pixels on either edge.`);
  }
  info.width = width;
  info.height = height;
  info.hdr = info.hasHdrMetadata;
  return info;
}

function validateNameAndMime(file) {
  const name = String(file?.name || '');
  const type = String(file?.type || '').toLowerCase();
  const hasAvifExtension = /\.avif$/i.test(name);
  const genericMime = !type || type === 'application/octet-stream' || type === 'binary/octet-stream';
  if (!hasAvifExtension && type !== 'image/avif') throw new Error('Choose an AVIF file with an .avif extension or image/avif MIME type.');
  if (!genericMime && type !== 'image/avif') throw new Error(`The file MIME type ${type} does not match AVIF.`);
}

async function validateFile(file) {
  if (!file || file.size === 0) throw new Error('The file is empty.');
  if (file.size > MAX_FILE_BYTES) throw new Error(`The file is larger than ${MAX_FILE_BYTES / (1024 * 1024)} MB.`);
  validateNameAndMime(file);
  const bytes = new Uint8Array(await file.arrayBuffer());
  return parseAvif(bytes);
}

function getStatusLabel(status) {
  return { queued: 'Ready', working: 'Converting…', success: 'Converted', error: 'Could not convert' }[status] || status;
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function setStatus(message, kind = '') {
  elements.status.textContent = message;
  elements.status.className = `status-message${kind ? ` ${kind}` : ''}`;
}

function makeEntry(file) {
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    file,
    status: 'queued',
    error: '',
    metadata: null,
    outputBlob: null,
    outputUrl: null,
    previewUrl: null,
    outputSize: 0,
    width: 0,
    height: 0,
    runId: 0,
  };
}

function revokeEntry(entry) {
  if (entry.outputUrl) URL.revokeObjectURL(entry.outputUrl);
  if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
  entry.outputUrl = null;
  entry.previewUrl = null;
  entry.outputBlob = null;
  entry.outputSize = 0;
}

function totalBytes() {
  return state.queue.reduce((sum, entry) => sum + entry.file.size, 0);
}

function outputExtension(format) {
  return format === 'jpg' ? '.jpg' : '.png';
}

function outputName(entry, format = state.format) {
  const base = entry.file.name.replace(/\.[^.]+$/, '') || 'converted-avif';
  return `${base}${outputExtension(format)}`;
}

function buildQueueItem(entry) {
  const item = document.createElement('article');
  item.className = 'queue-item';
  item.dataset.id = entry.id;

  const thumb = document.createElement('div');
  thumb.className = 'queue-thumb';
  if (entry.previewUrl) {
    const image = document.createElement('img');
    image.src = entry.previewUrl;
    image.alt = `Preview of ${entry.file.name}`;
    thumb.append(image);
  } else {
    thumb.textContent = entry.file.name.toLowerCase().endsWith('.avif') ? 'AVIF' : 'FILE';
  }

  const details = document.createElement('div');
  details.className = 'queue-details';
  const name = document.createElement('div');
  name.className = 'queue-name';
  name.title = entry.file.name;
  name.textContent = entry.file.name;
  const meta = document.createElement('div');
  meta.className = 'queue-meta';
  const dimensions = entry.width && entry.height ? ` · ${entry.width} × ${entry.height}` : '';
  const hdr = entry.metadata?.hdr ? ' · HDR metadata detected' : '';
  const outputSize = entry.status === 'success' && entry.outputSize ? ` · output ${formatBytes(entry.outputSize)}` : '';
  meta.textContent = `${formatBytes(entry.file.size)}${dimensions}${hdr}${outputSize}`;
  const status = document.createElement('div');
  status.className = `queue-status ${entry.status}`;
  status.textContent = entry.status === 'error' ? entry.error : getStatusLabel(entry.status);
  details.append(name, meta, status);

  const actions = document.createElement('div');
  actions.className = 'queue-actions';
  if (entry.status === 'success') {
    const download = document.createElement('button');
    download.className = 'mini-button';
    download.type = 'button';
    download.textContent = 'Download';
    download.addEventListener('click', () => downloadEntry(entry));
    actions.append(download);

    const reconvert = document.createElement('button');
    reconvert.className = 'mini-button';
    reconvert.type = 'button';
    reconvert.textContent = 'Convert again';
    reconvert.addEventListener('click', () => reconvertEntry(entry));
    actions.append(reconvert);
  }
  if (entry.status === 'error') {
    const retry = document.createElement('button');
    retry.className = 'mini-button';
    retry.type = 'button';
    retry.textContent = 'Retry';
    retry.addEventListener('click', () => retryEntry(entry));
    actions.append(retry);
  }
  const remove = document.createElement('button');
  remove.className = 'mini-button';
  remove.type = 'button';
  remove.textContent = 'Remove';
  remove.setAttribute('aria-label', `Remove ${entry.file.name}`);
  remove.addEventListener('click', () => removeEntry(entry.id));
  actions.append(remove);

  item.append(thumb, details, actions);
  return item;
}

function renderQueue() {
  elements.queueList.replaceChildren(...state.queue.map(buildQueueItem));
  const ready = state.queue.filter((entry) => entry.status === 'queued' && entry.metadata).length;
  const working = state.queue.filter((entry) => entry.status === 'working').length;
  const success = state.queue.filter((entry) => entry.status === 'success').length;
  const failed = state.queue.filter((entry) => entry.status === 'error').length;
  elements.queueCount.textContent = String(state.queue.length);
  elements.queueSummary.textContent = state.queue.length === 0
    ? 'Add a file to begin.'
    : `${ready} ready · ${working} working · ${success} complete${failed ? ` · ${failed} failed` : ''}`;
  elements.convertButton.disabled = ready === 0 || working > 0 || Boolean(state.activeRun);
  elements.clearButton.disabled = state.queue.length === 0;
  const settingLocked = working > 0 || Boolean(state.activeRun);
  document.querySelectorAll('input[name="output-format"]').forEach((input) => { input.disabled = settingLocked; });
  elements.qualityInput.disabled = settingLocked || state.format === 'png';
  const successes = state.queue.filter((entry) => entry.status === 'success' && entry.outputBlob);
  elements.resultPanel.hidden = successes.length === 0;
  elements.resultSummary.textContent = successes.length
    ? `${successes.length} ${successes.length === 1 ? 'file is' : 'files are'} ready as ${state.format.toUpperCase()}.`
    : '';
}

function cancelActiveRun() {
  if (state.activeRun) state.activeRun.cancelled = true;
}

function removeEntry(id) {
  const index = state.queue.findIndex((entry) => entry.id === id);
  if (index < 0) return;
  revokeEntry(state.queue[index]);
  state.queue.splice(index, 1);
  state.revision += 1;
  renderQueue();
  setStatus('File removed from the queue. Other files keep their current work.');
}

function clearQueue() {
  state.inputTicket += 1;
  cancelActiveRun();
  state.queue.forEach(revokeEntry);
  state.queue = [];
  state.revision += 1;
  renderQueue();
  setStatus('Queue cleared. Your originals were never uploaded.');
}

async function addFiles(fileList) {
  const incoming = [...fileList];
  if (!incoming.length) return;
  const ticket = ++state.inputTicket;
  if (state.queue.length + incoming.length > MAX_FILES) {
    setStatus(`A batch can contain up to ${MAX_FILES} files.`, 'error');
    return;
  }
  if (totalBytes() + incoming.reduce((sum, file) => sum + file.size, 0) > MAX_TOTAL_BYTES) {
    setStatus(`Keep the total batch below ${MAX_TOTAL_BYTES / (1024 * 1024)} MB.`, 'error');
    return;
  }

  const entries = [];
  for (const file of incoming) {
    if (ticket !== state.inputTicket) return;
    const entry = makeEntry(file);
    try {
      entry.metadata = await validateFile(file);
      entry.width = entry.metadata.width;
      entry.height = entry.metadata.height;
    } catch (error) {
      entry.status = 'error';
      entry.error = error instanceof Error ? error.message : 'The file could not be checked.';
    }
    entries.push(entry);
  }

  if (ticket !== state.inputTicket) return;
  if (state.queue.length + entries.length > MAX_FILES || totalBytes() + entries.reduce((sum, entry) => sum + entry.file.size, 0) > MAX_TOTAL_BYTES) {
    setStatus('The queue changed while the files were being checked. Add fewer files and try again.', 'error');
    return;
  }
  state.queue.push(...entries);
  state.revision += 1;
  renderQueue();
  if (state.activeRun && state.queue.some((entry) => entry.status === 'queued' && entry.metadata)) {
    setStatus('Previous conversions are finishing. Convert all will be available shortly.');
  } else if (state.queue.some((entry) => entry.status === 'queued' && entry.metadata)) {
    setStatus('Files are ready. Choose Convert all when you are ready.');
  }
  if (state.queue.some((entry) => entry.status === 'error')) setStatus('Some files need attention before conversion.', 'error');
  elements.fileInput.value = '';
}

function canvasBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('The browser could not create the output image.')), type, quality);
  });
}

function withTimeout(promise, message) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), DECODE_TIMEOUT_MS);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

async function decodeImage(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await withTimeout(createImageBitmap(file), 'The browser took too long to decode this AVIF. It was not converted.');
      return { image: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
    } catch (error) {
      if (error instanceof Error && error.message.includes('too long')) throw error;
      throw new Error('This browser could not decode this AVIF. Try a current browser with AVIF support.');
    }
  }
  if (typeof Image === 'undefined' || typeof URL?.createObjectURL !== 'function') throw new Error('This browser does not provide an AVIF image decoder.');
  const sourceUrl = URL.createObjectURL(file);
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    let settled = false;
    const timer = setTimeout(() => finish(new Error('The browser took too long to decode this AVIF. It was not converted.')), DECODE_TIMEOUT_MS);
    const cleanup = () => {
      clearTimeout(timer);
      URL.revokeObjectURL(sourceUrl);
      image.onload = null;
      image.onerror = null;
    };
    const finish = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (error) reject(error);
      else resolve({ image, width: image.naturalWidth, height: image.naturalHeight, close: () => { image.src = ''; } });
    };
    image.onload = () => {
      if (!image.naturalWidth || !image.naturalHeight) finish(new Error('The browser decoded an empty image.'));
      else finish();
    };
    image.onerror = () => finish(new Error('This browser could not decode this AVIF. Try a current browser with AVIF support.'));
    image.src = sourceUrl;
  });
}

function assertOutputBlob(blob, format) {
  const expectedType = format === 'jpg' ? 'image/jpeg' : 'image/png';
  if (!blob || blob.size === 0 || blob.type !== expectedType) throw new Error('The browser returned an unexpected output type or an empty file.');
  return blob.slice(0, 12).arrayBuffer().then((buffer) => {
    const bytes = new Uint8Array(buffer);
    const valid = format === 'jpg'
      ? bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff
      : bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47 && bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a;
    if (!valid) throw new Error('The browser returned invalid output image bytes.');
  });
}

function isActive(entry, run) {
  return state.activeRun === run && !run.cancelled && state.queue.includes(entry) && entry.runId === run.id;
}

async function createPreviewUrl(blob, width, height, format, quality) {
  if (Math.max(width, height) <= MAX_PREVIEW_EDGE) return URL.createObjectURL(blob);
  const scale = MAX_PREVIEW_EDGE / Math.max(width, height);
  const previewWidth = Math.max(1, Math.round(width * scale));
  const previewHeight = Math.max(1, Math.round(height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = previewWidth;
  canvas.height = previewHeight;
  let bitmap = null;
  let sourceUrl = null;
  let image = null;
  try {
    const context = canvas.getContext('2d', { alpha: true });
    if (!context) throw new Error('The browser could not create a preview canvas.');
    if (typeof createImageBitmap === 'function') {
      bitmap = await withTimeout(createImageBitmap(blob), 'The browser took too long to create the preview.');
      context.drawImage(bitmap, 0, 0, previewWidth, previewHeight);
    } else if (typeof Image !== 'undefined' && typeof URL?.createObjectURL === 'function') {
      sourceUrl = URL.createObjectURL(blob);
      image = new Image();
      image.decoding = 'async';
      await withTimeout(new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(new Error('The browser could not decode the converted preview.'));
        image.src = sourceUrl;
      }), 'The browser took too long to create the preview.');
      context.drawImage(image, 0, 0, previewWidth, previewHeight);
    } else {
      return URL.createObjectURL(blob);
    }
    const previewBlob = await canvasBlob(canvas, format === 'jpg' ? 'image/jpeg' : 'image/png', format === 'jpg' ? quality : undefined);
    await assertOutputBlob(previewBlob, format);
    return URL.createObjectURL(previewBlob);
  } finally {
    bitmap?.close?.();
    if (image) {
      image.onload = null;
      image.onerror = null;
      image.src = '';
    }
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    canvas.width = 0;
    canvas.height = 0;
  }
}

async function convertEntry(entry, run) {
  if (!isActive(entry, run) || !entry.metadata) return;
  entry.runId = run.id;
  entry.status = 'working';
  entry.error = '';
  renderQueue();
  let image = null;
  try {
    image = await decodeImage(entry.file);
    if (!isActive(entry, run)) return;
    const width = image.width || image.image.naturalWidth;
    const height = image.height || image.image.naturalHeight;
    if (!width || !height || width > MAX_DIMENSION || height > MAX_DIMENSION || width * height > MAX_PIXELS) throw new Error(`The decoded image must be at most ${MAX_PIXELS / 1000000} megapixels and ${MAX_DIMENSION} pixels on either edge.`);
    entry.width = width;
    entry.height = height;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    try {
      const context = canvas.getContext('2d', { alpha: true });
      if (!context) throw new Error('The browser could not create a conversion canvas.');
      if (run.format === 'jpg') {
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, height);
      }
      context.drawImage(image.image || image, 0, 0, width, height);
      const blob = await canvasBlob(canvas, run.format === 'jpg' ? 'image/jpeg' : 'image/png', run.format === 'jpg' ? run.quality : undefined);
      await assertOutputBlob(blob, run.format);
      if (!isActive(entry, run)) return;
      const previewUrl = await createPreviewUrl(blob, width, height, run.format, run.quality);
      if (!isActive(entry, run)) {
        URL.revokeObjectURL(previewUrl);
        return;
      }
      revokeEntry(entry);
      entry.outputBlob = blob;
      entry.outputSize = blob.size;
      entry.outputUrl = URL.createObjectURL(blob);
      entry.previewUrl = previewUrl;
      entry.status = 'success';
      state.revision += 1;
    } finally {
      canvas.width = 0;
      canvas.height = 0;
    }
  } catch (error) {
    if (!isActive(entry, run)) return;
    entry.status = 'error';
    entry.error = error instanceof Error ? error.message : 'The file could not be converted.';
    state.revision += 1;
  } finally {
    image?.close?.();
  }
  renderQueue();
}

async function convertAll() {
  if (state.activeRun) return;
  const pending = state.queue.filter((entry) => entry.status === 'queued' && entry.metadata);
  if (!pending.length) return;
  const run = {
    id: ++state.nextRunId,
    format: state.format,
    quality: state.quality,
    cancelled: false,
  };
  state.activeRun = run;
  pending.forEach((entry) => { entry.runId = run.id; });
  setStatus('Converting locally. Your files stay in this browser tab.');
  let cursor = 0;
  const worker = async () => {
    while (!run.cancelled) {
      const entry = pending[cursor];
      cursor += 1;
      if (!entry) return;
      await convertEntry(entry, run);
    }
  };
  await Promise.all(Array.from({ length: MAX_CONCURRENCY }, worker));
  if (run.cancelled) {
    if (state.activeRun === run) state.activeRun = null;
    renderQueue();
    if (state.queue.some((entry) => entry.status === 'queued' && entry.metadata)) setStatus('Previous conversions finished. Choose Convert all when you are ready.');
    return;
  }
  if (state.activeRun !== run) return;
  state.activeRun = null;
  const succeeded = state.queue.filter((entry) => entry.status === 'success').length;
  const failed = state.queue.filter((entry) => entry.status === 'error').length;
  setStatus(failed ? `${succeeded} converted; ${failed} need attention.` : `${succeeded} ${succeeded === 1 ? 'file is' : 'files are'} ready to download.`);
  renderQueue();
}

function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function downloadEntry(entry) {
  if (entry.outputBlob) downloadBlob(entry.outputBlob, outputName(entry));
}

async function downloadZip() {
  const successes = state.queue.filter((entry) => entry.status === 'success' && entry.outputBlob);
  if (!successes.length) return;
  const revision = state.revision;
  elements.zipButton.disabled = true;
  setStatus('Building the ZIP locally…');
  try {
    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    const usedNames = new Set();
    successes.forEach((entry) => {
      const original = outputName(entry);
      let name = original;
      let suffix = 2;
      while (usedNames.has(name)) name = `${original.replace(/\.[^.]+$/, '')}-${suffix++}${outputExtension(state.format)}`;
      usedNames.add(name);
      zip.file(name, entry.outputBlob);
    });
    const blob = await zip.generateAsync({ type: 'blob', compression: 'STORE' });
    if (revision !== state.revision) {
      setStatus('ZIP canceled because the queue changed.');
      return;
    }
    downloadBlob(blob, `kivoza-avif-converted-${state.format}.zip`);
    setStatus('ZIP downloaded. It was created in this browser tab.');
  } catch (error) {
    setStatus(error instanceof Error ? error.message : 'The ZIP could not be created.', 'error');
  } finally {
    elements.zipButton.disabled = false;
  }
}

async function retryEntry(entry) {
  if (!state.queue.includes(entry) || state.activeRun) return;
  const ticket = ++state.inputTicket;
  setStatus(`Checking ${entry.file.name} again…`);
  try {
    const metadata = await validateFile(entry.file);
    if (ticket !== state.inputTicket || !state.queue.includes(entry)) return;
    entry.metadata = metadata;
    entry.width = metadata.width;
    entry.height = metadata.height;
    entry.error = '';
    entry.status = 'queued';
    state.revision += 1;
    renderQueue();
    await convertAll();
  } catch (error) {
    if (ticket !== state.inputTicket || !state.queue.includes(entry)) return;
    entry.metadata = null;
    entry.status = 'error';
    entry.error = error instanceof Error ? error.message : 'The file could not be checked.';
    state.revision += 1;
    renderQueue();
    setStatus('The file still needs attention.', 'error');
  }
}

function reconvertEntry(entry) {
  if (!state.queue.includes(entry) || state.activeRun) return;
  revokeEntry(entry);
  entry.status = 'queued';
  entry.error = '';
  state.revision += 1;
  renderQueue();
  convertAll();
}

function resetForSettingChange() {
  if (state.activeRun) return;
  state.queue.forEach((entry) => {
    if (entry.status === 'error' && !entry.metadata) return;
    revokeEntry(entry);
    entry.status = 'queued';
    entry.error = '';
    entry.width = entry.metadata?.width || 0;
    entry.height = entry.metadata?.height || 0;
  });
  state.revision += 1;
  renderQueue();
  if (state.queue.length) setStatus('Output settings changed. Convert the queue again to create new files.');
}

elements.fileInput.addEventListener('change', (event) => addFiles(event.target.files));
elements.dropZone.addEventListener('click', (event) => {
  if (event.target !== elements.fileInput) elements.fileInput.click();
});
elements.dropZone.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    elements.fileInput.click();
  }
});
['dragenter', 'dragover'].forEach((eventName) => elements.dropZone.addEventListener(eventName, (event) => {
  event.preventDefault();
  elements.dropZone.classList.add('is-dragging');
}));
['dragleave', 'drop'].forEach((eventName) => elements.dropZone.addEventListener(eventName, (event) => {
  event.preventDefault();
  elements.dropZone.classList.remove('is-dragging');
}));
elements.dropZone.addEventListener('drop', (event) => addFiles(event.dataTransfer.files));
elements.convertButton.addEventListener('click', convertAll);
elements.clearButton.addEventListener('click', clearQueue);
elements.zipButton.addEventListener('click', downloadZip);
elements.qualityInput.addEventListener('input', () => {
  state.quality = Number(elements.qualityInput.value) / 100;
  elements.qualityValue.value = elements.qualityInput.value;
  elements.qualityValue.textContent = elements.qualityInput.value;
});
elements.qualityInput.addEventListener('change', resetForSettingChange);
document.querySelectorAll('input[name="output-format"]').forEach((input) => input.addEventListener('change', () => {
  state.format = document.querySelector('input[name="output-format"]:checked')?.value || 'jpg';
  elements.qualityControl.hidden = state.format === 'png';
  resetForSettingChange();
}));

renderQueue();
